import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { languages } from '../i18n';

export type LanguageSwitcherMenuPlacement = 'above' | 'below';

export interface LanguageSwitcherProps {
  /** `above` = opens upward (e.g. mobile nav). `below` = opens downward (e.g. desktop footer). */
  menuPlacement?: LanguageSwitcherMenuPlacement;
  /** Horizontal alignment of the menu panel */
  menuAlign?: 'center' | 'start';
}

export function LanguageSwitcher({
  menuPlacement = 'above',
  menuAlign = 'center',
}: LanguageSwitcherProps = {}) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  const horizontal =
    menuAlign === 'start' ? 'left-0' : 'left-1/2 -translate-x-1/2';
  const vertical =
    menuPlacement === 'above'
      ? 'bottom-full mb-2'
      : 'top-full mt-2';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs lg:text-sm text-content/60 hover:text-content rounded-lg hover:bg-gray-100 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Language"
      >
        <Globe className="h-3.5 w-3.5" aria-hidden />
        <span>{currentLang.flag} {currentLang.label}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Choose language"
          className={`absolute ${vertical} ${horizontal} w-44 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[200]`}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={lang.code === i18n.language}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                lang.code === i18n.language
                  ? 'bg-accent-text/10 text-accent-text font-medium'
                  : 'text-content hover:bg-gray-50'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
